import React, { useEffect, useState } from 'react'
import { verifyDisclosureCommitments, getDisclosureAuditBundle, prepareCommitment, commitSigned } from '../../services/api'
import { loadKeypair, signPayload } from '../../services/disclosureKeys'

interface ChainEntry {
  level: number
  expected_commitment: string
  stored_commitment: string
  verified: boolean
  history_hash_match: boolean
  chain_link_match: boolean
  signed?: boolean
  org_a_sig_valid?: boolean | null
  org_b_sig_valid?: boolean | null
}

interface Props {
  agreementId: string
  onClose: () => void
  orgId?: string
}

const CommitmentVerifyModal: React.FC<Props> = ({ agreementId, onClose, orgId }) => {
  const [chain, setChain] = useState<ChainEntry[]>([])
  const [allVerified, setAllVerified] = useState<boolean | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [keypair, setKeypair] = useState<CryptoKeyPair | null>(null)
  const [signing, setSigning] = useState(false)
  const [signError, setSignError] = useState<string | null>(null)

  useEffect(() => {
    verifyDisclosureCommitments(agreementId)
      .then(result => {
        setChain(result.chain)
        setAllVerified(result.all_verified)
      })
      .catch(() => setError('Failed to verify commitments'))
      .finally(() => setLoading(false))
  }, [agreementId])

  useEffect(() => {
    if (orgId) {
      loadKeypair(orgId).then(setKeypair).catch(() => null)
    }
  }, [orgId])

  const downloadAuditBundle = async () => {
    try {
      const bundle = await getDisclosureAuditBundle(agreementId)
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `audit-bundle-${agreementId}.json`
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      alert('Failed to download audit bundle')
    }
  }

  const tick = <span className="text-green-600 font-bold">✓</span>
  const cross = <span className="text-red-600 font-bold">✗</span>

  // null/undefined MUST be checked first — Phase 1 legacy entries have null sig_valid fields
  const sigCell = (val?: boolean | null) =>
    val === null || val === undefined
      ? <span className="text-gray-500">—</span>
      : val ? tick : cross

  const bothSignedCell = (entry: ChainEntry) =>
    entry.signed
      ? (entry.org_a_sig_valid && entry.org_b_sig_valid ? tick : cross)
      : <span className="text-gray-500">—</span>

  const allSignedVerified =
    chain.length > 0 &&
    chain.every(c => c.signed && c.org_a_sig_valid === true && c.org_b_sig_valid === true)

  const handleSign = async () => {
    if (!orgId || !keypair) return
    setSigning(true)
    setSignError(null)
    try {
      const prep = await prepareCommitment(agreementId)
      // v2 simplification: same sig used for both org slots (demo flow)
      const mySig = await signPayload(keypair.privateKey, prep.payload_bytes_hex)
      await commitSigned(agreementId, mySig, mySig, prep.next_level, prep.advanced_at)
      const result = await verifyDisclosureCommitments(agreementId)
      setChain(result.chain)
      setAllVerified(result.all_verified)
    } catch {
      setSignError('Failed to sign commitment — ensure both parties have registered keypairs')
    } finally {
      setSigning(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Commitment Chain Verification</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        {loading && <p className="text-gray-500">Verifying...</p>}
        {error && <p className="text-red-500">{error}</p>}

        {!loading && !error && (
          <>
            <div className={`mb-4 p-3 rounded ${allVerified ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
              {allVerified ? '✓ All commitments verified' : '✗ Chain integrity failure detected'}
            </div>

            {chain.length === 0 ? (
              <p className="text-gray-500 text-sm">No commitments recorded yet.</p>
            ) : (
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-2">Level</th>
                    <th className="text-left p-2">Verified</th>
                    <th className="text-left p-2">History Match</th>
                    <th className="text-left p-2">Chain Link</th>
                    <th className="text-left p-2">Org A Signed</th>
                    <th className="text-left p-2">Org B Signed</th>
                    <th className="text-left p-2">Both Signed</th>
                    <th className="text-left p-2">Commitment</th>
                  </tr>
                </thead>
                <tbody>
                  {chain.map((entry, i) => (
                    <tr key={i} className="border-b hover:bg-gray-50">
                      <td className="p-2">{entry.level}</td>
                      <td className="p-2">{entry.verified ? tick : cross}</td>
                      <td className="p-2">{entry.history_hash_match ? tick : cross}</td>
                      <td className="p-2">{entry.chain_link_match ? tick : cross}</td>
                      <td className="p-2">{sigCell(entry.org_a_sig_valid)}</td>
                      <td className="p-2">{sigCell(entry.org_b_sig_valid)}</td>
                      <td className="p-2">{bothSignedCell(entry)}</td>
                      <td className="p-2 font-mono text-xs text-gray-500 truncate max-w-xs" title={entry.stored_commitment}>
                        {entry.stored_commitment?.slice(0, 16)}…
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {signError && <p className="mt-2 text-sm text-red-500">{signError}</p>}

            <div className="mt-4 flex justify-end gap-2">
              {!allSignedVerified && keypair && orgId && (
                <button
                  onClick={handleSign}
                  disabled={signing}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm disabled:opacity-50"
                >
                  {signing ? 'Signing…' : 'Sign commitment'}
                </button>
              )}
              <button
                onClick={downloadAuditBundle}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
              >
                Download audit bundle
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default CommitmentVerifyModal
